import { computeThumbSize } from './thumbnails';

export const ORIGINAL_MAX_DIM = 4096;
const QUALITY = 0.9;

type Canvas = OffscreenCanvas | HTMLCanvasElement;

interface ShrinkDeps {
  createBitmap?: (bytes: Blob, options: ImageBitmapOptions) => Promise<ImageBitmap>;
  makeCanvas?: (w: number, h: number) => Canvas;
  encode?: (canvas: Canvas, quality: number) => Promise<Blob | null>;
}

function makeCanvas(w: number, h: number): Canvas {
  if (typeof OffscreenCanvas !== 'undefined') return new OffscreenCanvas(w, h);
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  return canvas;
}

function encode(canvas: Canvas, quality: number): Promise<Blob | null> {
  if ('convertToBlob' in canvas) return canvas.convertToBlob({ type: 'image/jpeg', quality });
  return new Promise((resolve) => canvas.toBlob(resolve, 'image/jpeg', quality));
}

/**
 * Scales a photo whose long edge exceeds ORIGINAL_MAX_DIM down to it. A smaller photo is
 * returned as is, and so is one that cannot be decoded or encoded here (HEIC outside Safari),
 * so the photo is kept.
 */
export async function shrinkOriginal(bytes: Blob, deps: ShrinkDeps = {}): Promise<Blob> {
  try {
    // The encode drops the EXIF Orientation tag, so the pixels must be upright before drawing.
    const bitmap = await (deps.createBitmap ?? createImageBitmap)(bytes, {
      imageOrientation: 'from-image',
    });
    let canvas: Canvas;
    try {
      if (Math.max(bitmap.width, bitmap.height) <= ORIGINAL_MAX_DIM) return bytes;
      const { width, height } = computeThumbSize(bitmap.width, bitmap.height, ORIGINAL_MAX_DIM);
      canvas = (deps.makeCanvas ?? makeCanvas)(width, height);
      const ctx = canvas.getContext('2d') as
        | CanvasRenderingContext2D
        | OffscreenCanvasRenderingContext2D
        | null;
      if (!ctx) throw new Error('2d context unavailable');
      ctx.drawImage(bitmap, 0, 0, width, height);
    } finally {
      bitmap.close();
    }
    const out = await (deps.encode ?? encode)(canvas, QUALITY);
    if (!out) throw new Error('encode failed');
    return out;
  } catch (err) {
    console.warn('photo not shrunk, uploading the original', err);
    return bytes;
  }
}
